import React, { useState, useEffect } from 'react';
import { X, ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusViewer = ({ customerId, onClose }) => {
  const [allStatuses, setAllStatuses] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    fetchStatuses();
  }, []);

  useEffect(() => {
    // Auto-progress timer (10 seconds per status)
    if (allStatuses.length === 0) return;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          handleNext();
          return 0;
        }
        return prev + 1; // 1% every 100ms = 10 seconds total
      });
    }, 100);

    return () => clearInterval(timer);
  }, [currentIndex, allStatuses]);

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API}/status/all`);
      console.log('✅ Fetched statuses:', response.data);
      console.log('🔍 Backend URL:', BACKEND_URL);
      
      // Log each status for debugging
      response.data.forEach((status, index) => {
        console.log(`Status ${index}:`, {
          type: status.type,
          image_url: status.image_url,
          video_url: status.video_url,
          full_image_path: status.image_url ? `${BACKEND_URL}${status.image_url}` : null
        });
      });
      
      setAllStatuses(response.data);
      
      // Mark first status as viewed
      if (response.data.length > 0) {
        markAsViewed(response.data[0].id);
      }
    } catch (error) {
      console.error('❌ Failed to fetch statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsViewed = async (statusId) => {
    try {
      await axios.post(`${API}/status/${statusId}/view`, null, {
        params: { customer_id: customerId }
      });
    } catch (error) {
      console.error('Failed to mark status as viewed:', error);
    }
  };

  const handleNext = () => {
    if (currentIndex < allStatuses.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      setProgress(0);
      markAsViewed(allStatuses[nextIndex].id);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setProgress(0);
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffHrs > 0) return `${diffHrs}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
        <p className="text-white">Loading statuses...</p>
      </div>
    );
  }

  if (allStatuses.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-50 p-4">
        <p className="text-white text-lg mb-4">No active statuses</p>
        <button
          onClick={onClose}
          className="px-6 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors"
        >
          Close
        </button>
      </div>
    );
  }

  const currentStatus = allStatuses[currentIndex];

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col" onClick={(e) => {
      // Tap the right half to go next, left half to go previous — WhatsApp behaviour
      if (e.target.tagName === 'IMG' || e.target.closest('button')) return;
      const w = window.innerWidth;
      if (e.clientX > w * 0.5) handleNext(); else handlePrevious();
    }}>
      {/* Header with progress bars */}
      <div className="px-3 pt-2 pb-3 bg-gradient-to-b from-black/70 to-transparent absolute top-0 left-0 right-0 z-20">
        {/* Progress Bars — WhatsApp style thin white segments */}
        <div className="flex gap-1 mb-2">
          {allStatuses.map((_, index) => (
            <div key={index} className="flex-1 h-[2px] bg-white/30 rounded-full overflow-hidden">
              <div
                className="h-full bg-white transition-all duration-100"
                style={{
                  width: index === currentIndex ? `${progress}%` : index < currentIndex ? '100%' : '0%'
                }}
              />
            </div>
          ))}
        </div>

        {/* User Info & Close — mirrors WhatsApp layout exactly */}
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="text-white p-1 hover:bg-white/10 rounded-full"
            data-testid="status-viewer-back"
          >
            <ArrowLeft size={22} weight="bold" />
          </button>
          <div className="w-9 h-9 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
            {currentStatus.user_avatar ? (
              <img src={currentStatus.user_avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-bold text-sm">
                {currentStatus.user_name?.charAt(0).toUpperCase() || 'A'}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm truncate">{currentStatus.user_name}</p>
            <p className="text-gray-300 text-[11px]">{getTimeAgo(currentStatus.created_at)}</p>
          </div>
        </div>
      </div>

      {/* Status Content — full-height black canvas, photo centered without cropping.
          IMPORTANT: `min-h-0` is required so the flex-1 child can actually
          shrink to the parent's height (default `min-height: auto` was letting
          tall portrait status images overflow past the viewport). */}
      <div className="flex-1 min-h-0 flex items-center justify-center relative overflow-hidden">
        {currentStatus.type === 'text' && currentStatus.content ? (
          <div className="bg-gradient-to-br from-green-600 to-green-700 w-full h-full flex items-center justify-center p-8">
            <p className="text-white text-2xl font-medium leading-relaxed text-center max-w-2xl">
              {currentStatus.content}
            </p>
          </div>
        ) : currentStatus.type === 'video' && currentStatus.video_url ? (
          <video
            key={currentStatus.id}
            src={currentStatus.video_url.startsWith('http') ? currentStatus.video_url : `${BACKEND_URL}${currentStatus.video_url.startsWith('/api') ? currentStatus.video_url : '/api' + currentStatus.video_url}`}
            autoPlay
            playsInline
            controls
            className="max-w-full max-h-full w-auto h-auto object-contain"
            onEnded={handleNext}
          />
        ) : currentStatus.type === 'image' && currentStatus.image_url ? (
          <img
            src={currentStatus.image_url.startsWith('http') ? currentStatus.image_url : `${BACKEND_URL}${currentStatus.image_url.startsWith('/api') ? currentStatus.image_url : '/api' + currentStatus.image_url}`}
            alt="Status"
            className="max-w-full max-h-full w-auto h-auto object-contain"
            style={{ display: 'block' }}
            data-testid="status-viewer-image"
            onError={(e) => {
              console.error('Image load error for status:', currentStatus.image_url);
              e.target.style.display = 'none';
            }}
          />
        ) : currentStatus.type === 'audio' && currentStatus.audio_url ? (
          <div className="bg-[#1f2c34] p-8 rounded-2xl max-w-sm mx-auto text-center">
            <audio
              src={currentStatus.audio_url.startsWith('http') ? currentStatus.audio_url : `${BACKEND_URL}${currentStatus.audio_url}`}
              controls
              autoPlay
              className="w-full"
              onEnded={handleNext}
            />
          </div>
        ) : (
          <div className="text-gray-400 p-8 text-center">
            <p>Content not available</p>
          </div>
        )}
      </div>

      {/* Caption — WhatsApp-style translucent bar UNDER the media so both
          image + text stay fully visible on any aspect-ratio screen. */}
      {currentStatus.caption && (currentStatus.type === 'image' || currentStatus.type === 'video') && (
        <div className="flex-shrink-0 bg-black/70 px-4 py-3 z-10" data-testid="status-viewer-caption-wrap">
          <p className="text-white text-center text-[15px] leading-snug whitespace-pre-line" data-testid="status-viewer-caption">
            {currentStatus.caption}
          </p>
        </div>
      )}
    </div>
  );
};

export default StatusViewer;
